import type { CSSProperties } from 'react';
import { useEffect, useMemo, useState } from 'react';

import mime from 'mime/lite';

import { useHtmlElement } from '../../hooks';
import { useIntersection } from '../../hooks/useIntersection';
import type { PictureSources } from './types';

const FALLBACK_TYPES = [
  'image/jpg',
  'image/jpeg',
  'image/png',
  'image/tiff',
  'image/gif',
];

/** @deprecated An ordered array of sources, most likely in different formats */
export type DeprecatedSources = string[];

interface PictureSharedProps {
  /** Explicit fallback src */
  src?: string;
  /** Required alt image tag */
  alt: string;
  /**
   * A union of loading types. Use `lazy` to defer loading of images outside of
   * the viewport and `eager` to load them immediately. For more information see
   * this resource.
   *
   * https://developer.mozilla.org/en-US/docs/Web/HTML/Element/img#attr-loading
   */
  loading?: 'lazy' | 'eager';
  /**
   * RootMargin to use for the intersection observer if you're lazy loading the
   * image
   */
  rootMargin?: string;
  /** Optional className */
  className?: string;
  /** Optional style attribute */
  style?: CSSProperties;
  /** Fetch priority hint for the browser */
  fetchPriority?: 'high' | 'low' | 'auto';
}

interface PicturePropsWithDeprecatedSources extends PictureSharedProps {
  sources: DeprecatedSources;
}

interface PicturePropsWithOptimizedSources extends PictureSharedProps {
  /**
   * An ordered array of sources in different formats. Each source should have a
   * `src`, `type`, and `media` property.
   */
  sources: PictureSources[];
}

export type PictureProps = PictureSharedProps & {
  sources: DeprecatedSources | PictureSources[];
};

export function Picture(props: PictureProps) {
  if (getIsOptimizedPictureProps(props)) {
    return <OptimizedPicture_ {...props} />;
  }
  return <Picture_ {...(props as PicturePropsWithDeprecatedSources)} />;
}

/**
 * The `Picture` component allows you to render images in next gen formats and
 * incorporate fallback sources.
 *
 * @see https://corgi-x.tfd.engineering/components/picture
 */
export function Picture_({
  sources,
  src,
  alt,
  loading = 'eager',
  rootMargin = '33%',
  ...props
}: PicturePropsWithDeprecatedSources) {
  const [loaded, setLoaded] = useState(loading !== 'lazy');
  const [imageEl, imageElRef] = useHtmlElement();
  const intersection = useIntersection(imageEl, {
    rootMargin,
  });

  useEffect(() => {
    if (loaded || !intersection) {
      return;
    }

    if (intersection.isIntersecting) {
      setLoaded(true);
    }
  }, [loaded, intersection]);

  const fallbackSrc = useMemo(() => {
    if (src) {
      return src;
    }

    return Array.isArray(sources)
      ? sources.find(srcSet => {
          const type = mime.getType(srcSet);
          return type && FALLBACK_TYPES.includes(type);
        })
      : '';
  }, [src, sources]);

  if (!Array.isArray(sources)) {
    return (
      <img
        ref={imageElRef}
        src={loaded ? fallbackSrc : undefined}
        alt={alt}
        {...props}
      />
    );
  }

  return (
    <picture>
      {sources.map((srcSet, index) => {
        const type = mime.getType(srcSet) || undefined;

        return (
          <source
            key={`${alt}-${type}-${index}`}
            type={type}
            srcSet={loaded ? srcSet : undefined}
          />
        );
      })}
      {/* important not to give img tag a src or safari double downloads */}
      <img ref={imageElRef} alt={alt} {...props} />
    </picture>
  );
}

function OptimizedPicture_({
  sources,
  alt,
  rootMargin = '33%',
  loading = 'eager',
  src,
  ...props
}: PicturePropsWithOptimizedSources) {
  const [loaded, setLoaded] = useState(loading !== 'lazy');
  const [imageEl, imageElRef] = useHtmlElement();
  const intersection = useIntersection(imageEl, {
    rootMargin,
  });

  useEffect(() => {
    if (loaded || !intersection) {
      return;
    }

    if (intersection.isIntersecting) {
      setLoaded(true);
    }
  }, [loaded, intersection]);

  if (!Array.isArray(sources)) {
    return (
      <img
        ref={imageElRef}
        src={loaded ? src : undefined}
        alt={alt}
        {...props}
      />
    );
  }
  return (
    <picture>
      {sources.map(({ src: srcSet, type, media }) => {
        return (
          <source
            key={srcSet}
            type={type}
            srcSet={srcSet}
            media={media}
            sizes="100vw"
          />
        );
      })}
      <img
        alt={alt}
        className={props.className}
        style={props.style}
        loading={loading}
        fetchPriority={props.fetchPriority}
      />
    </picture>
  );
}

function getIsOptimizedPictureProps(
  props: PictureProps
): props is PicturePropsWithOptimizedSources {
  return Array.isArray(props.sources) && typeof props.sources[0] !== 'string';
}
